import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '../../services/toastService'
import { adminAPI, entityAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useEntityDetailActions } from '../../hooks/admin/useEntityDetailActions'
import { createLogger } from '../../utils/logger'
import AdminHeader from '../../components/admin/AdminHeader'
import EntityDetailHeader from './components/EntityDetailHeader'
import EntityDetailForm from './components/EntityDetailForm'
import EntitySightingsList from './components/EntitySightingsList'
import EntityMetadata from './components/EntityMetadata'
import EntityVendorsSection from './components/EntityVendorsSection'

export default function EntityDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isEditMode = id && id !== 'new'
  const { loading, error, data: entity, execute: executeEntity } = useAsyncOperation()
  const { loading: loadingSightings, data: sightingsData, execute: executeSightings } = useAsyncOperation()
  const [editing, setEditing] = useState(!isEditMode)
  const [formData, setFormData] = useState({
    name: '',
    entityType: 'food_truck',
    category: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    imageUrl: '',
    currentAddress: '',
    currentLatitude: null,
    currentLongitude: null,
    isActive: true
  })
  const [sightings, setSightings] = useState([])
  const [sightingsPagination, setSightingsPagination] = useState({ page: 1, totalPages: 1, total: 0 })
  const [sightingsSearch, setSightingsSearch] = useState('')
  const [sightingsSearchInput, setSightingsSearchInput] = useState('')
  const [sightingsFilter, setSightingsFilter] = useState('all')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [mapPosition, setMapPosition] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  // Initialize map position
  useEffect(() => {
    if (formData.currentLatitude && formData.currentLongitude) {
      setMapPosition({ lat: formData.currentLatitude, lng: formData.currentLongitude })
    }
  }, [])

  // Load entity and sightings
  useEffect(() => {
    if (isEditMode) {
      loadEntity()
      loadSightings(1)
    }
  }, [id])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSightingsSearch(sightingsSearchInput)
      setSightingsPagination(prev => ({ ...prev, page: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [sightingsSearchInput])

  // Reload when search/filter changes
  useEffect(() => {
    if (isEditMode) {
      loadSightings(1)
    }
  }, [sightingsSearch, sightingsFilter, isEditMode])

  const loadEntity = async () => {
    const entityData = await executeEntity(
      async () => {
        const response = await adminAPI.getEntity(id)
        return response.data
      },
      { errorMessage: 'Failed to load entity details' }
    )
    
    if (entityData) {
      setFormData({
        name: entityData.name || '',
        entityType: entityData.type || 'food_truck',
        category: entityData.category || '',
        description: entityData.description || '',
        email: entityData.email || '',
        phone: entityData.phone || '',
        website: entityData.website || '',
        imageUrl: entityData.imageUrl || '',
        currentAddress: entityData.currentAddress || '',
        currentLatitude: entityData.currentLatitude || null,
        currentLongitude: entityData.currentLongitude || null,
        isActive: entityData.isActive ?? true
      })
    }
  }

  const loadSightings = async (page = 1) => {
    await executeSightings(
      async () => {
        const params = { page, limit: 20 }
        const response = await entityAPI.getEntityLocations(id, params)
        const sightingsArray = response.data.data || []
        
        let filteredData = sightingsArray
        if (sightingsSearch.trim()) {
          const searchLower = sightingsSearch.trim().toLowerCase()
          filteredData = sightingsArray.filter(s => 
            (s.address && s.address.toLowerCase().includes(searchLower)) ||
            (s.notes && s.notes.toLowerCase().includes(searchLower))
          )
        }
        
        setSightings(filteredData)
        setSightingsPagination({
          page: response.data.page || page,
          totalPages: response.data.totalPages || response.data.total_pages || 1,
          total: response.data.total || 0
        })
        
        return filteredData
      },
      { silent: true }
    )
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters'
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }
    
    if (formData.phone && formData.phone.trim().length > 0 && formData.phone.trim().length < 7) {
      errors.phone = 'Phone number seems too short'
    }
    
    if (formData.website && formData.website.trim().length > 0) {
      try {
        new URL(formData.website)
      } catch {
        errors.website = 'Invalid website URL (must start with http:// or https://)'
      }
    }
    
    if (formData.imageUrl && formData.imageUrl.trim().length > 0) {
      try {
        new URL(formData.imageUrl)
      } catch {
        errors.imageUrl = 'Invalid image URL'
      }
    }
    
    if (formData.currentLatitude !== null && formData.currentLatitude !== '') {
      const lat = parseFloat(formData.currentLatitude)
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.currentLatitude = 'Latitude must be between -90 and 90'
      }
    }
    
    if (formData.currentLongitude !== null && formData.currentLongitude !== '') {
      const lng = parseFloat(formData.currentLongitude)
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.currentLongitude = 'Longitude must be between -180 and 180'
      }
    }
    
    if (!formData.category || formData.category.trim().length < 2) {
      errors.category = 'Category is required'
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Extract all handlers to custom hook
  const {
    handleImageUpload,
    handleMapPositionChange,
    handleSave,
    handleDelete,
    handleVerify,
    handleCancel
  } = useEntityDetailActions({
    entityId: id,
    isEditMode,
    entity,
    loadEntity,
    executeEntity,
    formData,
    setFormData,
    setMapPosition,
    setUploadingImage,
    setEditing,
    setValidationErrors,
    validateForm
  })

  // Loading state
  if (loading && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('app.loading')}</div>
      </div>
    )
  }

  // Not found state
  if (!entity && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Entity not found</p>
          <button
            onClick={() => navigate('/admin/entities')}
            className="text-primary hover:text-primary-600"
          >
            {t('admin.backToCollection')}
          </button>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <EntityDetailHeader
            isEditMode={isEditMode}
            editing={editing}
            entity={entity}
            onEdit={() => setEditing(true)}
          onSave={handleSave}
          onCancel={handleCancel}
          onVerify={handleVerify}
          onDelete={handleDelete}
          loading={loading}
        />

        <EntityDetailForm
          formData={formData}
          onChange={setFormData}
          validationErrors={validationErrors}
          editing={editing || !isEditMode}
          isEditMode={isEditMode}
          onImageUpload={handleImageUpload}
          uploadingImage={uploadingImage}
          mapPosition={mapPosition}
          onMapPositionChange={handleMapPositionChange}
          showMapPicker={showMapPicker}
          setShowMapPicker={setShowMapPicker}
          entity={entity}
        />

        {isEditMode && entity && (
          <EntitySightingsList
            sightings={sightings}
            loading={loadingSightings}
            searchInput={sightingsSearchInput}
            onSearchChange={setSightingsSearchInput}
            filter={sightingsFilter}
            onFilterChange={setSightingsFilter}
            pagination={sightingsPagination}
            onPageChange={loadSightings}
          />
        )}

        {isEditMode && entity && <EntityVendorsSection entityId={id} />}

        {isEditMode && entity && <EntityMetadata entity={entity} />}
        </div>
      </div>
    </div>
  )
}
